import { v4 as uuidv4 } from "uuid";
import axios from "axios";
import { auth, authStateReady } from "../Config/firebase-config";
import { fontList } from "../Config/config";
import { FontSyncStatus, UploadedFontModel } from "../types/customFontTypes";
import { parseAndValidateFont } from "./fontParser";
import FavoriteFontManager from "./favoriteFontManager";
import UploadMedia from "../actions/UploadMedia/uploadMedia";
import store from "../Redux/store";
import { resetDeletedFont } from "../Redux/WaterMarks/watermarks";

const DB_NAME = "ezy_fonts_db";
const STORE_NAME = "custom_fonts";
const DB_VERSION = 1;

type FontUpdateListener = (fonts: UploadedFontModel[]) => void;

export class UploadedFontManager {
  private static instance: UploadedFontManager;
  private db: IDBDatabase | null = null;
  private uploadedFonts: UploadedFontModel[] = [];
  private registeredFontFaces = new Set<string>();
  private listeners: Set<FontUpdateListener> = new Set();
  private isSyncing = false;
  private initialized = false;
  private initPromise: Promise<UploadedFontModel[]> | null = null;

  private constructor() {
    if (typeof window !== "undefined" && typeof indexedDB !== "undefined") {
      auth.onAuthStateChanged((user) => {
        if (!user) {
          void this.clearOnLogout();
          FavoriteFontManager.clearOnLogout();
        } else {
          this.initialized = false;
          void this.loadAndRegisterAllUploadedFonts();
        }
      });
    }
  }

  public static getInstance(): UploadedFontManager {
    if (!UploadedFontManager.instance) {
      UploadedFontManager.instance = new UploadedFontManager();
    }
    return UploadedFontManager.instance;
  }

  // MARK: - Subscriptions

  public subscribe(listener: FontUpdateListener): () => void {
    this.listeners.add(listener);
    listener(this.uploadedFonts);
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners(): void {
    const list = [...this.uploadedFonts];
    this.listeners.forEach((listener) => {
      try {
        listener(list);
      } catch (err) {
        console.error("Font listener error:", err);
      }
    });
  }

  // MARK: - IndexedDB Storage

  private async openDatabase(): Promise<IDBDatabase> {
    if (this.db) return this.db;

    return new Promise((resolve, reject) => {
      if (typeof window === "undefined" || !window.indexedDB) {
        reject(new Error("IndexedDB is not available in this environment"));
        return;
      }

      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;
        if (!db.objectStoreNames.contains(STORE_NAME)) {
          db.createObjectStore(STORE_NAME, { keyPath: "id" });
        }
      };

      request.onsuccess = () => {
        this.db = request.result;
        resolve(request.result);
      };

      request.onerror = () => {
        reject(request.error || new Error("Failed to open IndexedDB"));
      };
    });
  }

  private async getAllFromDB(): Promise<UploadedFontModel[]> {
    const db = await this.openDatabase();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readonly");
      const store = tx.objectStore(STORE_NAME);
      const req = store.getAll();

      req.onsuccess = () => resolve(req.result || []);
      req.onerror = () => reject(req.error);
    });
  }

  private async saveToDB(font: UploadedFontModel): Promise<void> {
    const db = await this.openDatabase();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.put(font);

      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  private async deleteFromDB(id: string): Promise<void> {
    const db = await this.openDatabase();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_NAME, "readwrite");
      const store = tx.objectStore(STORE_NAME);
      const req = store.delete(id);

      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  // MARK: - Browser FontFace Registration

  public async registerFontInBrowser(postScriptName: string, data: Blob | ArrayBuffer): Promise<boolean> {
    if (typeof window === "undefined" || typeof document === "undefined" || !document.fonts) {
      return false;
    }

    try {
      let arrayBuffer: ArrayBuffer;
      if (data instanceof Blob) {
        arrayBuffer = await data.arrayBuffer();
      } else {
        arrayBuffer = data;
      }

      // Check if already registered with exact same postScriptName
      if (this.registeredFontFaces.has(postScriptName)) {
        return true;
      }

      const fontFace = new FontFace(postScriptName, arrayBuffer);
      await fontFace.load();
      document.fonts.add(fontFace);
      this.registeredFontFaces.add(postScriptName);

      return true;
    } catch (err) {
      console.warn(`Failed to register font face "${postScriptName}" with document.fonts:`, err);
      return false;
    }
  }

  public unregisterFontFromBrowser(postScriptName: string): void {
    if (typeof document === "undefined" || !document.fonts) return;

    try {
      document.fonts.forEach((fontFace) => {
        if (fontFace.family.replace(/['"]/g, "").toLowerCase() === postScriptName.toLowerCase()) {
          document.fonts.delete(fontFace);
        }
      });
      this.registeredFontFaces.delete(postScriptName);
    } catch (err) {
      console.warn(`Error unregistering font "${postScriptName}":`, err);
    }
  }

  // MARK: - Public Getters

  public getUploadedFonts(): UploadedFontModel[] {
    return [...this.uploadedFonts];
  }

  public findFontByPostScriptName(postScriptName: string): UploadedFontModel | undefined {
    const target = postScriptName.toLowerCase();
    return this.uploadedFonts.find(
      (f) =>
        f.postScriptName.toLowerCase() === target ||
        f.displayName.toLowerCase() === target ||
        f.fileName.toLowerCase() === target
    );
  }

  public findFontById(id: string): UploadedFontModel | undefined {
    return this.uploadedFonts.find((f) => f.id === id);
  }

  // MARK: - Load & Initialize

  public async clearOnLogout(): Promise<void> {
    for (const font of this.uploadedFonts) {
      this.unregisterFontFromBrowser(font.postScriptName);
    }
    this.registeredFontFaces.forEach((name) => {
      this.unregisterFontFromBrowser(name);
    });
    this.registeredFontFaces.clear();

    this.uploadedFonts = [];
    this.initialized = false;
    this.initPromise = null;

    try {
      const db = await this.openDatabase();
      await new Promise<void>((resolve, reject) => {
        const tx = db.transaction(STORE_NAME, "readwrite");
        const store = tx.objectStore(STORE_NAME);
        const req = store.clear();
        req.onsuccess = () => resolve();
        req.onerror = () => reject(req.error);
      });
    } catch (err) {
      console.warn("Failed to clear fonts from IndexedDB on logout:", err);
    }

    this.notifyListeners();
  }

  public async loadAndRegisterAllUploadedFonts(): Promise<UploadedFontModel[]> {
    if (!auth.currentUser) {
      this.uploadedFonts = [];
      this.initialized = true;
      this.notifyListeners();
      return [];
    }
    if (this.initialized) return this.uploadedFonts;
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      try {
        const allSaved = await this.getAllFromDB();
        const activeFonts = allSaved.filter((f) => f.status !== FontSyncStatus.DELETED);

        for (const font of activeFonts) {
          if (font.blob) {
            await this.registerFontInBrowser(font.postScriptName, font.blob);
          }
        }

        this.uploadedFonts = activeFonts;
        this.initialized = true;
        this.notifyListeners();

        // Trigger background sync if logged in and online
        if (navigator.onLine && auth.currentUser) {
          void this.syncPendingFontsInBackground();
        }

        return activeFonts;
      } catch (err) {
        console.error("Failed to load uploaded fonts from IndexedDB:", err);
        this.initialized = true;
        return [];
      } finally {
        this.initPromise = null;
      }
    })();

    return this.initPromise;
  }

  // MARK: - Import Font (Upload from user file)

  public async importFont(file: File): Promise<{ success: boolean; model?: UploadedFontModel; error?: string }> {
    // 1. Validate & extract metadata
    const validation = await parseAndValidateFont(file);
    if (!validation.valid || !validation.metadata) {
      return { success: false, error: validation.error || "Invalid font file." };
    }

    const { postScriptName, fullName, fontFamily } = validation.metadata;
    const displayName = fullName || fontFamily || postScriptName;
    const ext = file.name.split(".").pop()?.toLowerCase() || "ttf";

    // 2. Check for duplicates in active uploaded fonts
    const existingFont = this.uploadedFonts.find(
      (f) =>
        f.postScriptName.toLowerCase() === postScriptName.toLowerCase() ||
        f.fileName.toLowerCase() === file.name.toLowerCase()
    );

    if (existingFont) {
      const fontName = existingFont.displayName || displayName || existingFont.fileName;
      return { success: false, error: `"${fontName}" is already uploaded.` };
    }

    // Check against standard bundled fontList
    const isDuplicateSystem = fontList.some(
      (f) => f.toLowerCase() === displayName.toLowerCase() || f.toLowerCase() === postScriptName.toLowerCase()
    );
    if (isDuplicateSystem) {
      return { success: false, error: `"${displayName}" is already available in the default fonts list.` };
    }

    // 3. Register with browser FontFace for immediate rendering
    const arrayBuffer = await file.arrayBuffer();
    const registered = await this.registerFontInBrowser(postScriptName, arrayBuffer);
    if (!registered) {
      return { success: false, error: "Failed to register font with browser rendering engine." };
    }

    // 4. Create model with status = 'pending'
    const newModel: UploadedFontModel = {
      id: uuidv4(),
      postScriptName,
      displayName,
      fileName: file.name,
      fileSize: file.size,
      dateAdded: Math.floor(Date.now() / 1000),
      format: "ttf",
      status: FontSyncStatus.PENDING,
      blob: file,
    };

    // 5. Save to IndexedDB & memory
    try {
      await this.saveToDB(newModel);
      this.uploadedFonts.push(newModel);
      this.notifyListeners();
    } catch (err) {
      console.error("Failed to save imported font to IndexedDB:", err);
      return { success: false, error: "Failed to persist font to local storage." };
    }

    // 6. Trigger immediate cloud upload if logged in
    let finalModel = newModel;
    if (navigator.onLine && auth.currentUser) {
      try {
        finalModel = await this.uploadSingleFont(newModel);
      } catch (uploadErr) {
        console.warn("[UploadedFontManager] Immediate cloud upload error, will retry in background:", uploadErr);
      }
    }

    return { success: true, model: finalModel };
  }

  // MARK: - Delete Font

  public async deleteFont(model: UploadedFontModel): Promise<boolean> {
    // 1. Unregister from browser
    this.unregisterFontFromBrowser(model.postScriptName);

    // 2. Remove from in-memory list
    this.uploadedFonts = this.uploadedFonts.filter((f) => f.id !== model.id);
    this.notifyListeners();

    try {
      store.dispatch(
        resetDeletedFont({
          id: model.id,
          postScriptName: model.postScriptName,
          displayName: model.displayName,
          fileName: model.fileName,
        })
      );
    } catch {
      // ignore
    }

    // 3. Update or delete in IndexedDB
    try {
      const allSaved = await this.getAllFromDB();
      const existing = allSaved.find((f) => f.id === model.id);

      if (existing) {
        if (existing.status === FontSyncStatus.COMPLETED && existing.fileKey) {
          // Mark deleted to sync server deletion
          existing.status = FontSyncStatus.DELETED;
          await this.saveToDB(existing);

          if (navigator.onLine && auth.currentUser) {
            void this.deleteFontFromServer(existing.id);
          }
        } else {
          // Unsynced pending font, simply delete from DB
          await this.deleteFromDB(model.id);
        }
      }
    } catch (err) {
      console.error("Error updating IndexedDB on font delete:", err);
    }

    return true;
  }

  // MARK: - Background Cloud Sync Pipeline

  public async syncPendingFontsInBackground(): Promise<void> {
    if (this.isSyncing) return;
    await authStateReady();
    if (!navigator.onLine || !auth.currentUser) return;

    this.isSyncing = true;

    try {
      const allSaved = await this.getAllFromDB();

      // Step 1: Process pending deletions
      const deletedFonts = allSaved.filter((f) => f.status === FontSyncStatus.DELETED);
      for (const font of deletedFonts) {
        await this.deleteFontFromServer(font.id);
      }

      // Step 2: Process pending uploads
      const pendingUploads = allSaved.filter((f) => f.status === FontSyncStatus.PENDING);
      for (const font of pendingUploads) {
        await this.uploadSingleFont(font);
      }

      // Step 3: Fetch remote fonts to sync any fonts uploaded on mobile/iOS
      await this.fetchRemoteFonts();
    } catch (err) {
      console.warn("Background font sync error:", err);
    } finally {
      this.isSyncing = false;
    }
  }

  private async uploadSingleFont(fontModel: UploadedFontModel): Promise<UploadedFontModel> {
    if (!fontModel.blob) return fontModel;

    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) return fontModel;


      // Step 1: Request pre-signed upload URL
      const uploadUrlRes = await axios.post(
        "fonts/upload-url",
        { fileName: fontModel.fileName },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );

      const { uploadUrl, fileKey } = uploadUrlRes.data || {};
      if (!uploadUrl) return fontModel;

      // Step 2: Binary PUT upload to pre-signed URL using standard UploadMedia helper
      await UploadMedia(uploadUrl, fontModel.blob);

      // Step 3: Save record to DB via POST /fonts
      const saveRes = await axios.post(
        "fonts",
        {
          name: fontModel.displayName,
          fileName: fontModel.fileName,
          fileKey: fileKey || fontModel.fileKey || "",
        },
        { headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" } }
      );

      const serverRecord = saveRes.data || {};
      const serverId = serverRecord.id || fontModel.id;

      // Step 4: Update IndexedDB record to COMPLETED
      const updatedModel: UploadedFontModel = {
        ...fontModel,
        id: serverId,
        status: FontSyncStatus.COMPLETED,
        fileKey: serverRecord.fileKey || fileKey,
        userId: serverRecord.userId,
        createdAt: serverRecord.createdAt,
        updatedAt: serverRecord.updatedAt,
      };

      if (serverId !== fontModel.id) {
        await this.deleteFromDB(fontModel.id);
      }
      await this.saveToDB(updatedModel);

      // Update in-memory list
      const idx = this.uploadedFonts.findIndex((f) => f.id === fontModel.id || f.id === serverId);
      if (idx !== -1) {
        this.uploadedFonts[idx] = updatedModel;
      }
      this.notifyListeners();
      return updatedModel;
    } catch (err) {
      console.warn(`Font upload sync failed for "${fontModel.displayName}":`, err);
      return fontModel;
    }
  }

  private async deleteFontFromServer(fontId: string): Promise<void> {
    try {
      const token = await auth.currentUser?.getIdToken();
      if (!token) return;

      const deleteUrl = `fonts/${fontId}`;

      const delRes = await axios.delete(deleteUrl, {
        headers: { Authorization: `Bearer ${token}` },
      });

      await this.deleteFromDB(fontId);
    } catch (err) {
      console.warn(`Server delete failed for font ${fontId}:`, err);
    }
  }

  // MARK: - Fetch Remote Fonts (Cloud -> Web)

  public async fetchRemoteFonts(search?: string, page = 1, limit = 50): Promise<UploadedFontModel[]> {
    await authStateReady();
    if (!navigator.onLine || !auth.currentUser) return [];

    if (!this.initialized) {
      await this.loadAndRegisterAllUploadedFonts();
    }

    try {
      const token = await auth.currentUser.getIdToken();
      if (!token) return [];

      let url = `fonts/me?page=${page}&limit=${limit}`;
      if (search && search.trim()) {
        url += `&search=${encodeURIComponent(search.trim())}`;
      }

      const res = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
      });

      const fontsArray =
        res.data?.fonts ||
        (Array.isArray(res.data) ? res.data : res.data?.data || []);
      const remoteModels: UploadedFontModel[] = [];

      // Prune local fonts that were deleted from server (e.g. via mobile app)
      if (page === 1 && (!search || !search.trim())) {
        const remoteIds = new Set(fontsArray.map((item: any) => item.id).filter(Boolean));
        const remoteFileKeys = new Set(fontsArray.map((item: any) => item.fileKey).filter(Boolean));

        const staleFonts = this.uploadedFonts.filter(
          (local) =>
            local.status === FontSyncStatus.COMPLETED &&
            local.fileKey &&
            !remoteIds.has(local.id) &&
            !remoteFileKeys.has(local.fileKey)
        );

        for (const stale of staleFonts) {
          this.unregisterFontFromBrowser(stale.postScriptName);
          FavoriteFontManager.removeFavorite(stale.postScriptName);
          await this.deleteFromDB(stale.id);
        }

        if (staleFonts.length > 0) {
          const staleIds = new Set(staleFonts.map((f) => f.id));
          this.uploadedFonts = this.uploadedFonts.filter((f) => !staleIds.has(f.id));
        }
      }

      for (const item of fontsArray) {
        const id = item.id || "";
        const name = item.name || "";
        const fileKey = item.fileKey || "";
        const downloadUrl = item.downloadUrl || "";
        const fileName = fileKey ? fileKey.split("/").pop() || `${name}.ttf` : `${name}.ttf`;

        // Check if we already have this font locally in memory
        let local = this.uploadedFonts.find((f) => f.id === id || (fileKey && f.fileKey === fileKey));

        if (!local && downloadUrl) {
          try {
            // Download binary font file to cache in IndexedDB
            const fontBlobRes = await fetch(downloadUrl);
            const blob: Blob = await fontBlobRes.blob();

            let postScriptName = name.replace(/[\s_]+/g, "-");
            let displayName = name;
            try {
              const validation = await parseAndValidateFont(blob, fileName);
              if (validation.metadata?.postScriptName) {
                postScriptName = validation.metadata.postScriptName;
              }
              if (validation.metadata?.fullName || validation.metadata?.fontFamily) {
                displayName = validation.metadata.fullName || validation.metadata.fontFamily || name;
              }
            } catch {
              // fallback to name
            }

            await this.registerFontInBrowser(postScriptName, blob);

            local = {
              id,
              postScriptName,
              displayName,
              fileName,
              fileSize: blob.size,
              dateAdded: Math.floor(Date.now() / 1000),
              format: "ttf",
              status: FontSyncStatus.COMPLETED,
              fileKey,
              userId: item.userId,
              createdAt: item.createdAt,
              updatedAt: item.updatedAt,
              downloadUrl,
              blob,
            };

            await this.saveToDB(local);
            this.uploadedFonts.push(local);
          } catch (dlErr) {
            console.warn(`Failed to download remote font "${name}":`, dlErr);
          }
        } else if (local) {
          if (!local.blob && downloadUrl) {
            try {
              const fontBlobRes = await fetch(downloadUrl);
              const blob: Blob = await fontBlobRes.blob();
              local.blob = blob;
              local.fileSize = blob.size;
              await this.registerFontInBrowser(local.postScriptName, blob);
              await this.saveToDB(local);
            } catch (dlErr) {
              console.warn(`Failed to re-download blob for "${name}":`, dlErr);
            }
          } else if (local.blob) {
            await this.registerFontInBrowser(local.postScriptName, local.blob);
          }
          if (id && !local.id) local.id = id;
          if (downloadUrl) local.downloadUrl = downloadUrl;
        }

        if (local) {
          remoteModels.push(local);
        }
      }

      this.notifyListeners();
      return remoteModels;
    } catch (err) {
      console.warn("Failed to fetch remote fonts from server:", err);
      return [];
    }
  }
}
export default UploadedFontManager.getInstance();
